import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkle, Warning, Lightning, X } from '@phosphor-icons/react';
import api from '../lib/api';

/**
 * Global sticky top-of-app trial countdown.
 *  - Visible on every page (except super-admin and the billing page itself).
 *  - Color logic:
 *      > 7 days  → green (calm)
 *      3–7 days  → amber (nudge)
 *      < 3 days  → red (urgent)
 *      expired   → solid red + dominant CTA
 *  - Polls /api/billing/subscription every 60s and on tab focus.
 *  - Hides for active paid plans (status === 'active' AND non-trial plan).
 */
export default function TrialBanner() {
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const load = async () => {
    try {
      const r = await api.get('/billing/subscription');
      setSub(r.data || null);
    } catch (_e) { console.debug('[caught]', _e); /* silent — banner just hides */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, []);

  // Hide on super-admin or billing pages — they have their own state visualisations.
  if (sub?.is_super_admin) return null;
  if (location.pathname.startsWith('/super-admin')) return null;
  if (location.pathname.startsWith('/billing')) return null;

  if (!sub) return null;

  const status = sub?.subscription?.status || '';
  const days = typeof sub?.days_remaining === 'number' ? sub.days_remaining : null;
  const isTrial = status === 'trialing';
  const isExpired = status === 'expired' || (days !== null && days < 0 && !sub.active);
  const isActive = sub.active && !isTrial;

  // Hide entirely once the customer is on a paid active plan.
  if (isActive) return null;
  if (dismissed && !isExpired) return null;

  const tone = isExpired
    ? { bg: 'bg-red-600', fg: 'text-white', sub: 'text-red-100', icon: <Warning size={18} weight="fill" />, dot: 'bg-red-300' }
    : days === null || days >= 8
      ? { bg: 'bg-emerald-50 border-b border-emerald-200', fg: 'text-emerald-900', sub: 'text-emerald-700', icon: <Sparkle size={16} weight="fill" className="text-emerald-600" />, dot: 'bg-emerald-400' }
      : days >= 3
        ? { bg: 'bg-amber-50 border-b border-amber-200', fg: 'text-amber-900', sub: 'text-amber-700', icon: <Sparkle size={16} weight="fill" className="text-amber-600" />, dot: 'bg-amber-400' }
        : { bg: 'bg-red-50 border-b border-red-200', fg: 'text-red-900', sub: 'text-red-700', icon: <Warning size={16} weight="fill" className="text-red-600" />, dot: 'bg-red-500' };

  const message = isExpired
    ? 'Your trial has expired — upgrade to continue using all features.'
    : days === 1
      ? 'Your trial expires tomorrow. Upgrade to continue using all features.'
      : `Your trial expires in ${days} day${days === 1 ? '' : 's'}. Upgrade to continue using all features.`;

  return (
    <div
      data-testid="trial-banner"
      data-trial-status={isExpired ? 'expired' : days >= 8 ? 'green' : days >= 3 ? 'amber' : 'red'}
      className={`w-full px-3 sm:px-5 py-2 flex items-center gap-3 sticky top-0 z-40 ${tone.bg}`}
    >
      <span className={`hidden sm:inline-flex items-center justify-center h-6 w-6 rounded-full ${isExpired ? 'bg-white/15' : 'bg-white/60'}`}>
        {tone.icon}
      </span>

      <div className="flex-1 min-w-0">
        <p className={`text-xs sm:text-sm font-semibold leading-tight ${tone.fg}`}>{message}</p>
        {!isExpired && (
          <p className={`hidden sm:block text-[10px] mt-0.5 leading-tight ${tone.sub}`}>
            Pick a plan now — keep your team's access uninterrupted.
          </p>
        )}
      </div>

      <button
        onClick={() => navigate('/billing')}
        data-testid="trial-banner-upgrade-btn"
        className={`whitespace-nowrap px-3 sm:px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all
          ${isExpired
            ? 'bg-white text-red-700 hover:bg-red-50 shadow'
            : 'bg-[#6B4D5F] text-white hover:bg-[#5A3D4E]'}`}
      >
        <Lightning size={13} weight="fill" /> Upgrade Now
      </button>

      {!isExpired && (
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss trial banner"
          data-testid="trial-banner-dismiss-btn"
          className={`hidden sm:inline-flex items-center justify-center h-6 w-6 rounded-full hover:bg-black/5 ${tone.sub}`}
        >
          <X size={13} />
        </button>
      )}
    </div>
  );
}
